import React, { useEffect, useRef, useState } from "react";
import sal from "sal.js";
import { Turnstile } from "@marsidev/react-turnstile";

const ComplaintBook = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // Turnstile
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // Inicializa las animaciones de scroll con sal.js
  useEffect(() => {
    sal();
  }, []);

  /**
   * Maneja el cambio en el input de archivo.
   * Valida el tipo y tamaño del archivo antes de guardarlo.
   * @param {Event} e - Evento del input file.
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB en bytes
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(file.type)) {
        alert("Formato no permitido. Solo se aceptan JPG, PNG, DOCX y PDF.");
        e.target.value = ""; // Limpia el input
        return;
      }

      if (file.size > maxSize) {
        alert("El archivo debe ser menor a 5MB.");
        e.target.value = ""; // Limpia el input
        return;
      }

      // Guarda el archivo si es válido
      setSelectedFile(file);
    }
  };

  /**
   * Maneja el envío del formulario.
   * Recopila los datos del formulario y el archivo (si existe) en un objeto FormData
   * y realiza una petición POST al endpoint correspondiente.
   * @param {Event} e - Evento de envío del formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertMessage("");

    if (!turnstileToken) {
      setAlertMessage(
        "Verificación de seguridad pendiente. Intenta nuevamente."
      );
      // intenta regenerar token por si el widget quedó “quieto”
      turnstileRef.current?.reset?.();
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    const formElements = e.target.elements;
    formData.append("website", formElements["website"].value);
    formData.append("document_type", formElements["document-type"].value);
    formData.append("document_number", formElements["document-number"].value);
    formData.append("name", formElements["name"].value);
    formData.append("email", formElements["email"].value);
    formData.append("phone_number", formElements["phone-number"].value || "");
    formData.append("parent_name", formElements["parent-name"].value || "");
    formData.append("address", formElements["address"].value || "");
    formData.append("amount_claim", formElements["amount-claim"].value);
    formData.append("description", formElements["description"].value);
    formData.append("request_type", formElements["request-type"].value);
    formData.append("detail", formElements["detail"].value);
    formData.append("request", formElements["request"].value);

    // Turnstile token
    formData.append("turnstile_token", turnstileToken);

    // Si se seleccionó un archivo, se incluye en el FormData
    if (selectedFile) {
      formData.append("attachment", selectedFile);
    }

    try {
      const response = await fetch(`${apiUrl}/contact/send-complaint`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error al enviar el reclamo.");
      }

      const data = await response.json();
      setAlertMessage(data.message || "¡Reclamo enviado exitosamente!");

      // Resetea el formulario y el archivo seleccionado
      e.target.reset();
      setSelectedFile(null);
    } catch (error) {
      setAlertMessage(
        error.message ||
          "Hubo un problema al enviar el reclamo. Por favor, inténtalo de nuevo."
      );
    } finally {
      setIsSubmitting(false);

      // Regenerar Turnstile para permitir otro envío
      setTurnstileToken(null);
      turnstileRef.current?.reset?.();
    }
  };

  return (
    <div className="rainbow-pricing-area rainbow-section-gap">
      <div className="container-fluid">
        <div className="wrapper rainbow-contact-area">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="section-title text-center"
                  data-sal="slide-up"
                  data-sal-duration="400"
                  data-sal-delay="150"
                >
                  <h2 className="subtitle text-2xl font-bold">
                    <span className="theme-gradient">
                      Libro de reclamaciones
                    </span>
                  </h2>
                </div>
              </div>
            </div>

            {/* Formulario de contacto */}
            <div className="row mt--40 row--15">
              <div className="contact-details-box overflow-hidden">
                <form
                  onSubmit={handleSubmit}
                  className="rbt-profile-row rbt-default-form row row--15"
                  encType="multipart/form-data"
                >
                  <div
                    aria-hidden="true"
                    style={{ position: "absolute", left: "-10000px" }}
                  >
                    <label htmlFor="website">Sitio web</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  {/* Mensaje de alerta */}
                  {alertMessage && (
                    <div className="col-12">
                      <div className="form-group">
                        <span className="alert-message">{alertMessage}</span>
                      </div>
                    </div>
                  )}

                  <h4 className="pl--15">
                    1. Identificación del consumidor reclamante
                  </h4>
                  <div className="rbt-sm-separator mt-0"></div>

                  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="document-type">
                        Tipo de documento (*)
                      </label>
                      <div className="rbt-modern-select bg-transparent height-45">
                        <select
                          id="document-type"
                          name="document-type"
                          required
                          className="w-100"
                        >
                          <option value="">Seleccionar</option>
                          <option value="DNI">DNI</option>
                          <option value="Carné de extranjería">
                            Carné de extranjería
                          </option>
                          <option value="Otros">Otros</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="document-number">
                        Nro. de documento (*)
                      </label>
                      <input
                        id="document-number"
                        name="document-number"
                        type="text"
                        required
                        maxLength="15"
                      />
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="name">Nombre (*)</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        maxLength="100"
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="email">Correo electrónico (*)</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        maxLength="100"
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="phone-number">Teléfono</label>
                      <input
                        id="phone-number"
                        name="phone-number"
                        type="tel"
                        maxLength="20"
                      />
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="parent-name">
                        Padre o madre (en caso de ser menor de edad)
                      </label>
                      <input
                        id="parent-name"
                        name="parent-name"
                        type="text"
                        maxLength="100"
                      />
                    </div>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="form-group">
                      <label htmlFor="address">Dirección</label>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        maxLength="200"
                      />
                    </div>
                  </div>

                  <h4 className="pl--15 mt-5">
                    2. Identificación del bien contratado
                  </h4>
                  <div className="rbt-sm-separator mt-0"></div>

                  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label>Categoría</label>
                      <div className="rbt-modern-select bg-transparent height-45">
                        <select name="category" className="w-100">
                          <option value="Servicio">Servicio</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                    <div className="form-group">
                      <label htmlFor="amount-claim">Monto reclamado (*)</label>
                      <input
                        id="amount-claim"
                        name="amount-claim"
                        type="number"
                        step="0.01"
                        required
                        maxLength="5"
                      />
                    </div>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="form-group">
                      <label htmlFor="description">
                        Descripción del bien contratado (*)
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows="3"
                        minLength="10"
                        maxLength="4000"
                      ></textarea>
                    </div>
                  </div>

                  <h4 className="pl--15 mt-5">3. Detalle de la reclamación</h4>
                  <div className="rbt-sm-separator mt-0"></div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="form-group">
                      <label>Tipo (*)</label>
                      <div className="rbt-modern-select bg-transparent height-45">
                        <select
                          id="request-type"
                          name="request-type"
                          required
                          className="w-100"
                        >
                          <option value="">Seleccionar</option>
                          <option value="Reclamo">Reclamo &sup1;</option>
                          <option value="Queja">Queja &sup2;</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="form-group">
                      <label htmlFor="detail">Detalle (*)</label>
                      <textarea
                        id="detail"
                        name="detail"
                        required
                        rows="3"
                        minLength="10"
                        maxLength="4000"
                      ></textarea>
                    </div>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="form-group">
                      <label htmlFor="request">Pedido (*)</label>
                      <textarea
                        id="request"
                        name="request"
                        required
                        rows="3"
                        minLength="10"
                        maxLength="4000"
                      ></textarea>
                    </div>
                  </div>

                  <div className="col-lg-12 col-md-12 col-sm-12 col-12">
                    <div className="form-group">
                      <label htmlFor="attachment">Documento o imagen</label>
                      <div className="modern-file">
                        <input
                          id="attachment"
                          name="attachment"
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf,.docx"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={siteKey}
                      onSuccess={(token) => setTurnstileToken(token)}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                    />
                  </div>

                  <div className="col-12 mt--20">
                    <div className="form-group mb--0">
                      <button
                        className="btn-default"
                        type="submit"
                        disabled={isSubmitting || !turnstileToken}
                      >
                        Enviar{" "}
                        {isSubmitting && (
                          <i className="feather-loader spinner-icon"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintBook;
